import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Users, Link2, Globe, CheckCircle2, Network, ShieldCheck, Zap } from 'lucide-react';
type Audience = 'personal' | 'team';
export function AudienceSwitch() {
  const [audience, setAudience] = useState<Audience>('personal');
  return <section className="w-full bg-[#FAF8F4] py-20 md:py-32 border-b border-neutral-200/70 relative overflow-hidden" data-id="element-334">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-335">
        {/* Toggle */}
        <div className="flex justify-center mb-16 md:mb-24" data-id="element-336">
          <div className="relative flex items-center p-1.5 bg-white border border-neutral-200/80 rounded-full shadow-sm" data-id="element-337">
            <div className="absolute inset-y-1.5 w-[calc(50%-6px)] bg-[#FAF8F4] border border-neutral-200/60 rounded-full shadow-sm transition-all duration-300 ease-out" style={{
            left: audience === 'personal' ? '6px' : 'calc(50%)'
          }} data-id="element-338" />
            <button onClick={() => setAudience('personal')} className={`relative z-10 flex items-center gap-2 px-6 md:px-8 py-3 rounded-full text-sm md:text-base font-semibold transition-colors duration-300 ${audience === 'personal' ? 'text-[#2B211A]' : 'text-neutral-500 hover:text-[#2B211A]'}`} data-id="element-339">
              <User size={18} className={audience === 'personal' ? 'text-[#EA580C]' : ''} data-id="element-340" />
              Giga for You
            </button>
            <button onClick={() => setAudience('team')} className={`relative z-10 flex items-center gap-2 px-6 md:px-8 py-3 rounded-full text-sm md:text-base font-semibold transition-colors duration-300 ${audience === 'team' ? 'text-[#2B211A]' : 'text-neutral-500 hover:text-[#2B211A]'}`} data-id="element-341">
              <Users size={18} className={audience === 'team' ? 'text-[#EA580C]' : ''} data-id="element-342" />
              Giga for Your Team
            </button>
          </div>
        </div>

        {/* Swappable Content */}
        <div className="relative min-h-[400px]" data-id="element-343">
          <AnimatePresence mode="wait" data-id="element-344">
            {audience === 'personal' ? <motion.div key="personal" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -20
          }} transition={{
            duration: 0.4,
            ease: 'easeOut'
          }} className="flex flex-col items-center" data-id="element-345">
                <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-346">
                  <h2 className="text-3xl md:text-5xl font-title font-medium text-[#2B211A] tracking-tight leading-[1.1] mb-6" data-id="element-347">
                    Your most capable copilot.
                  </h2>
                  <p className="text-lg md:text-xl text-neutral-500 leading-relaxed" data-id="element-348">
                    The more you ask of it, the more it does for you. Giga
                    connects to everything you use and actually finishes the
                    job.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full max-w-5xl" data-id="element-349">
                  <FeatureCard visualType="connectors" title="More connectors" description="Plugs into your entire stack. If it has an API, Giga can talk to it and take action on your behalf." data-id="element-350" />
                  <FeatureCard visualType="utilities" title="Real utilities" description="Access databases, run browser sessions, and use real tools. Giga doesn't just chat, it operates." data-id="element-351" />
                  <FeatureCard visualType="finishes" title="It actually finishes things" description="Most AI stops at the suggestion. Giga takes the action and hands back the finished result." data-id="element-352" />
                </div>
              </motion.div> : <motion.div key="team" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -20
          }} transition={{
            duration: 0.4,
            ease: 'easeOut'
          }} className="flex flex-col items-center" data-id="element-353">
                <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-354">
                  <h2 className="text-3xl md:text-5xl font-title font-medium text-[#2B211A] tracking-tight leading-[1.1] mb-6" data-id="element-355">
                    Your company's shared brain.
                  </h2>
                  <p className="text-lg md:text-xl text-neutral-500 leading-relaxed" data-id="element-356">
                    Every interaction maps the org in real time. Everyone's work
                    makes the whole organization smarter.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full max-w-5xl" data-id="element-357">
                  <FeatureCard visualType="map" title="Workspace Map" description="A living graph of your org. Configure where Giga looks for things, who owns what, and what's in flight." data-id="element-358" />
                  <FeatureCard visualType="permissions" title="Enterprise Permissions" description="Agents and users share the exact same permission boundaries. Nothing leaks, nothing's over-permissioned." data-id="element-359" />
                  <FeatureCard visualType="dissemination" title="Skill Dissemination" description="One user writes a prompt or knowledge artifact; all users benefit. Expertise compounds instead of staying siloed." data-id="element-360" />
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </section>;
}
function AnimatedVisual({
  type
}: {
  type: string;
}) {
  if (type === 'connectors') {
    return <div className="relative flex items-center justify-center w-full h-full" data-id="element-361">
        <motion.div className="absolute w-12 h-12 rounded-full border-[3px] border-[#EA580C]/80" animate={{
        x: [-10, 0, -10],
        scale: [1, 1.05, 1]
      }} transition={{
        duration: 3,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-362" />
        <motion.div className="absolute w-12 h-12 rounded-full border-[3px] border-[#EA580C]/40" animate={{
        x: [10, 0, 10],
        scale: [1, 1.05, 1]
      }} transition={{
        duration: 3,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-363" />
      </div>;
  }
  if (type === 'utilities') {
    return <div className="relative flex items-center justify-center w-full h-full gap-2" data-id="element-364">
        {[0, 1, 2].map(i => <motion.div key={i} className="w-3 h-12 bg-[#EA580C]/20 rounded-full overflow-hidden relative" data-id="element-365">
            <motion.div className="absolute bottom-0 w-full bg-[#EA580C]" animate={{
          height: ['20%', '80%', '20%']
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: i * 0.2
        }} data-id="element-366" />
          </motion.div>)}
      </div>;
  }
  if (type === 'finishes') {
    return <div className="relative flex items-center justify-center w-full h-full" data-id="element-367">
        <svg width="48" height="48" viewBox="0 0 48 48" className="-rotate-90" data-id="element-368">
          <circle cx="24" cy="24" r="20" fill="none" stroke="#EA580C" strokeWidth="4" strokeOpacity="0.2" data-id="element-369" />
          <motion.circle cx="24" cy="24" r="20" fill="none" stroke="#EA580C" strokeWidth="4" strokeLinecap="round" initial={{
          pathLength: 0
        }} animate={{
          pathLength: 1
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut',
          repeatDelay: 1
        }} data-id="element-370" />
        </svg>
        <motion.div className="absolute" initial={{
        opacity: 0,
        scale: 0.5
      }} animate={{
        opacity: [0, 1, 1, 0],
        scale: [0.5, 1, 1, 0.5]
      }} transition={{
        duration: 3,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-371">
          <CheckCircle2 size={20} className="text-[#EA580C]" data-id="element-372" />
        </motion.div>
      </div>;
  }
  if (type === 'map') {
    return <div className="relative flex items-center justify-center w-full h-full" data-id="element-373">
        <motion.div className="absolute w-3 h-3 bg-[#EA580C] rounded-full" data-id="element-374" />
        {[0, 72, 144, 216, 288].map((angle, i) => <motion.div key={i} className="absolute w-full h-full flex items-center justify-center" animate={{
        rotate: [angle, angle + 360]
      }} transition={{
        duration: 20,
        repeat: Infinity,
        ease: 'linear'
      }} data-id="element-375">
            <motion.div className="absolute w-2 h-2 bg-[#EA580C]/60 rounded-full" style={{
          transform: `translateY(-24px)`
        }} animate={{
          scale: [1, 1.5, 1]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          delay: i * 0.4
        }} data-id="element-376" />
            <div className="absolute w-[1px] h-6 bg-[#EA580C]/20" style={{
          transform: `translateY(-12px)`
        }} data-id="element-377" />
          </motion.div>)}
      </div>;
  }
  if (type === 'permissions') {
    return <div className="relative flex items-center justify-center w-full h-full" data-id="element-378">
        {[1, 2, 3].map(i => <motion.div key={i} className="absolute rounded-full border border-[#EA580C]" style={{
        width: i * 20,
        height: i * 20
      }} animate={{
        opacity: [0.8, 0, 0.8],
        scale: [1, 1.2, 1]
      }} transition={{
        duration: 3,
        repeat: Infinity,
        delay: i * 0.3,
        ease: 'easeInOut'
      }} data-id="element-379" />)}
        <div className="absolute w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-sm" data-id="element-380">
          <ShieldCheck size={14} className="text-[#EA580C]" data-id="element-381" />
        </div>
      </div>;
  }
  if (type === 'dissemination') {
    return <div className="relative flex items-center justify-center w-full h-full" data-id="element-382">
        <motion.div className="absolute w-6 h-6 bg-[#EA580C] rounded-full blur-sm" animate={{
        scale: [1, 1.5, 1],
        opacity: [0.5, 0.8, 0.5]
      }} transition={{
        duration: 2,
        repeat: Infinity
      }} data-id="element-383" />
        <div className="absolute w-4 h-4 bg-[#EA580C] rounded-full z-10" data-id="element-384" />
        {[0, 1, 2, 3].map(i => <motion.div key={i} className="absolute w-full h-full border border-[#EA580C]/30 rounded-full" initial={{
        scale: 0.2,
        opacity: 1
      }} animate={{
        scale: 2,
        opacity: 0
      }} transition={{
        duration: 2,
        repeat: Infinity,
        delay: i * 0.5,
        ease: 'easeOut'
      }} data-id="element-385" />)}
      </div>;
  }
  return null;
}
function FeatureCard({
  visualType,
  title,
  description
}: {
  visualType: string;
  title: string;
  description: string;
}) {
  return <div className="bg-white/40 backdrop-blur-xl rounded-3xl p-8 border border-white/80 shadow-[0_8px_32px_rgba(43,33,26,0.04)] hover:shadow-[0_16px_48px_rgba(43,33,26,0.08)] hover:bg-white/50 transition-all duration-300 relative overflow-hidden group" data-id="element-386">
      {/* Subtle inner highlight for the liquid glass effect */}
      <div className="absolute inset-0 rounded-3xl border border-white/40 pointer-events-none" style={{
      mixBlendMode: 'overlay'
    }} data-id="element-387" />
      <div className="absolute -inset-1/2 bg-gradient-to-br from-white/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none blur-2xl" data-id="element-388" />

      <div className="relative z-10 flex flex-col h-full" data-id="element-389">
        <div className="w-full h-32 rounded-2xl bg-gradient-to-br from-white/60 to-white/20 backdrop-blur-md border border-white/80 flex items-center justify-center mb-8 shadow-sm overflow-hidden relative" data-id="element-390">
          <AnimatedVisual type={visualType} data-id="element-391" />
        </div>
        <h3 className="text-xl font-bold text-[#2B211A] mb-3" data-id="element-392">{title}</h3>
        <p className="text-neutral-500 leading-relaxed" data-id="element-393">{description}</p>
      </div>
    </div>;
}